/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useState, useEffect } from 'react';
import { Component, ComponentType } from '../../types';
import { apiClient } from '../../services/api';
import './PdfSearchModal.css';

interface PdfSearchModalProps {
  onClose: () => void;
  onSelectComponent: (component: Component) => void;
  excludedComponentIds: number[];
}

const PdfSearchModal: React.FC<PdfSearchModalProps> = ({ onClose, onSelectComponent, excludedComponentIds }) => {
  const [components, setComponents] = useState<Component[]>([]);
  const [filteredComponents, setFilteredComponents] = useState<Component[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchComponents();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredComponents(components);
    } else {
      const filtered = components.filter(component =>
        component.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (component.manufacturerName && component.manufacturerName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (component.manufacturerCode && component.manufacturerCode.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredComponents(filtered);
    }
  }, [searchTerm, components]);

  const fetchComponents = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.getAllComponents();
      if (response.success && response.components) {
        // Filter out components that are already selected
        const availableComponents = response.components.filter(component => !excludedComponentIds.includes(component.id));
        setComponents(availableComponents);
        setFilteredComponents(availableComponents);
      } else {
        setError(response.error || 'Failed to load components');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while loading components');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectComponent = (component: Component) => {
    onSelectComponent(component);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content pdf-search-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Search and Select Component</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by name, manufacturer, or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>

          {isLoading && <div className="loading">Loading components...</div>}
          {error && <div className="error-message">{error}</div>}

          {!isLoading && !error && (
            <div className="pdf-list">
              {filteredComponents.length === 0 ? (
                <div className="no-results">No components found</div>
              ) : (
                filteredComponents.map(component => (
                  <div
                    key={component.id}
                    className="pdf-item"
                    onClick={() => handleSelectComponent(component)}
                  >
                    <div className="pdf-item-name">
                      {component.name}
                      {component.type === ComponentType.COLLECTION && (
                        <span style={{
                          marginLeft: '0.5rem',
                          padding: '0.125rem 0.375rem',
                          backgroundColor: '#8b5cf6',
                          color: 'white',
                          fontSize: '0.7rem',
                          borderRadius: '4px',
                          fontWeight: 500
                        }}>
                          COLLECTION
                        </span>
                      )}
                    </div>
                    <div className="pdf-item-details">
                      {component.manufacturerName || component.manufacturerCode ? (
                        <>
                          <span>{component.manufacturerName || 'N/A'}</span>
                          <span className="separator">|</span>
                          <span>{component.manufacturerCode || 'N/A'}</span>
                        </>
                      ) : (
                        <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No manufacturer info</span>
                      )}
                      {component.price && (
                        <>
                          <span className="separator">|</span>
                          <span>${component.price.toFixed(2)}</span>
                        </>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="cancel-button" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default PdfSearchModal;
